import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { portalApi } from '../../api/portal';

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
}

function FunnelStat({ label, value, sub = null }) {
  return (
    <div>
      <p className="text-base font-semibold">{value ?? 0}</p>
      <p className="text-xs text-gray-500">
        {label}
        {sub ? ` · ${sub}` : ''}
      </p>
    </div>
  );
}

// Authenticated affiliate dashboard (#612). Gated client-side on the portal
// cookie session: if portalApi.me() fails (signed out / link expired), bounce
// to /login. The magic-link verify lands the affiliate here.
export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [links, setLinks] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    portalApi
      .me()
      .then((me) => {
        if (cancelled) return;
        setAffiliate(me);
        setLoading(false);
        return Promise.all([portalApi.links(), portalApi.earnings()])
          .then(([l, e]) => {
            if (cancelled) return;
            setLinks(l);
            setEarnings(e);
          })
          .catch((err) => {
            if (!cancelled) setError(err.message || 'Could not load your dashboard.');
          });
      })
      .catch(() => {
        // Not signed in (no/expired cookie session) — send them to sign in.
        if (!cancelled) navigate('/login', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const signOut = async () => {
    await portalApi.signOut().catch(() => {});
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }
  if (!affiliate) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <Link to="/" className="text-xl font-bold">
              ByScott Affiliates
            </Link>
            <p className="text-sm text-gray-500">
              {affiliate.name}
              {affiliate.email ? ` · ${affiliate.email}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/help" className="text-gray-600 hover:text-gray-900">
              Help
            </Link>
            <button onClick={signOut} className="text-gray-600 hover:text-gray-900">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
        )}

        {/* Earnings */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Earnings</h2>
          {!earnings ? (
            <p className="text-gray-500">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <Stat label="Lifetime" value={dollars(earnings.totals.lifetime_cents)} />
                <Stat label="Pending" value={dollars(earnings.totals.pending_cents)} />
                <Stat label="Paid out" value={dollars(earnings.totals.paid_cents)} />
              </div>
              {earnings.commissions.length > 0 && (
                <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                  {earnings.commissions.map((c) => (
                    <div key={c.id} className="px-4 py-2 flex justify-between text-sm">
                      <span>
                        {c.client_account_name || 'Referral'}
                        {c.accrued_at ? ` · ${new Date(c.accrued_at).toLocaleDateString()}` : ''}
                      </span>
                      <span>
                        <span className="font-medium">{dollars(c.amount_cents)}</span>{' '}
                        <span className="text-gray-500">{c.status}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* Links */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Your links</h2>
          {!links ? (
            <p className="text-gray-500">Loading…</p>
          ) : links.length === 0 ? (
            <p className="text-gray-500">
              No referral links yet — your account manager sets these up.
            </p>
          ) : (
            <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
              {links.map((l) => (
                <div key={`${l.app_name}-${l.code}`} className="px-4 py-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-mono text-sm">{l.code}</span>
                      {l.app_name && <span className="ml-2 text-sm text-gray-500">{l.app_name}</span>}
                      {!l.active && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                          inactive
                        </span>
                      )}
                      <p className="text-xs text-gray-500 mt-0.5">
                        {l.commission_percent > 0 ? `${l.commission_percent}% commission` : ''}
                        {l.commission_cents > 0
                          ? `${l.commission_percent > 0 ? ' + ' : ''}${dollars(l.commission_cents)} flat`
                          : ''}
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <p>
                        <span className="font-medium">{l.total_referrals}</span> referrals
                      </p>
                      <p className="text-gray-500">{l.active_referrals} active</p>
                    </div>
                  </div>

                  {/* Funnel overlay from metrics (#612): clicks → signups → conversions. */}
                  {l.funnel && (
                    <div className="mt-2 pt-2 border-t border-gray-50 grid grid-cols-3 gap-2 text-center">
                      <FunnelStat label="Clicks" value={l.funnel.clicks} />
                      <FunnelStat
                        label="Signups"
                        value={l.funnel.signups}
                        sub={l.funnel.clicks > 0 ? `${l.funnel.signup_rate}%` : null}
                      />
                      <FunnelStat
                        label="Conversions"
                        value={l.funnel.conversions}
                        sub={l.funnel.signups > 0 ? `${l.funnel.conversion_rate}%` : null}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
